<script setup lang="ts">
import { computed, ref, useTemplateRef } from 'vue';
import NativeDialog from '@/core/dialogsystem/NativeDialog.vue';
import IconButton from '../elements/IconButton.vue';
import { EIcon } from 'common/enum/misc.enum';
import { useUserRatingDialog } from '@/composables/userFeedback/useUserRatingDialog';
import { useI18n } from 'vue-i18n';
import { Components } from '@/api/generated/api';
import RatingFrontPage from './RatingFrontPage.vue';
import RatingSubmitted from './RatingSubmitted.vue';
import RatingFinal from './RatingFinal.vue';
import { useMobile } from '@/composables/useMobile';
import { useAnalytics } from '@/composables/analytics/useAnalytics';
import { AnalyticsEvents } from '@/services/analytics/events';

const dialogRef = useTemplateRef('dialogRef');
const ratingFrontPageRef = useTemplateRef('ratingFrontPageRef');
const { persistFeedback, handleDialogHide } = useUserRatingDialog();
const { t } = useI18n();
const { isMobile } = useMobile();
const { trackEvent } = useAnalytics();

const selectedStar = ref<number>(0);
const feedbackText = ref<string | null>(null);
const trigger = ref<string>('');
const persistedFeedbackId = ref<number | null>(null);
const starRatingSubmitted = ref<boolean>(false);
const feedbackTextSubmitted = ref<boolean>(false);
const ratingSubmitTracked = ref<boolean>(false);
const entity = ref<Components.Schemas.UserFeedbackEntity>();
const entityCount = ref<number>();

const resetDialogState = (): void => {
  ratingFrontPageRef.value?.resetState();
  selectedStar.value = 0;
  feedbackText.value = null;
  starRatingSubmitted.value = false;
  feedbackTextSubmitted.value = false;
  ratingSubmitTracked.value = false;
  trigger.value = '';
  entity.value = undefined;
  entityCount.value = undefined;
};

const buildRatingEventPayload = (extra: Record<string, unknown> = {}): Record<string, unknown> => ({
  trigger: trigger.value,
  ...(entity.value ? { entity: entity.value } : {}),
  ...(entityCount.value !== undefined ? { entityCount: entityCount.value } : {}),
  ...extra,
});

const show = ({
  triggerValue,
  entityValue,
  entityCountValue,
}: {
  triggerValue: string;
  entityValue?: Components.Schemas.UserFeedbackEntity;
  entityCountValue?: number;
}): void => {
  resetDialogState();
  trigger.value = triggerValue;
  entity.value = entityValue;
  entityCount.value = entityCountValue;
  trackEvent(AnalyticsEvents.rating.dialogOpen, buildRatingEventPayload());
  trackEvent(AnalyticsEvents.rating.imp, buildRatingEventPayload());
  dialogRef.value?.showModal();
};

const trackSubmitRating = (extra: Record<string, unknown> = {}): void => {
  if (ratingSubmitTracked.value) return;
  ratingSubmitTracked.value = true;
  trackEvent(AnalyticsEvents.rating.submitted, buildRatingEventPayload(extra));
};

const hide = async (): Promise<void> => {
  dialogRef.value?.close();
  await handleDialogHide(true);
  resetDialogState();
};

const submitTextFeedback = async (text: string): Promise<void> => {
  feedbackText.value = text;

  await persistFeedback(
    persistedFeedbackId.value,
    {
      rating: selectedStar.value,
      message: feedbackText.value,
      trigger: trigger.value,
      entity: entity.value,
      entityCount: entityCount.value,
    },
    feedbackText.value ? t('help.rating.sendSuccess') : undefined,
  );

  trackEvent(
    AnalyticsEvents.rating.feedbackSubmitted,
    buildRatingEventPayload({
      rating: selectedStar.value,
      hasMessage: text.trim().length > 0,
    }),
  );
  trackSubmitRating({
    hasMessage: text.trim().length > 0,
    rating: selectedStar.value,
    step: 'feedback',
  });

  feedbackTextSubmitted.value = true;
};

const submitStarRating = async (value: number): Promise<void> => {
  selectedStar.value = value;

  persistedFeedbackId.value = await persistFeedback(
    null,
    {
      rating: selectedStar.value,
      message: null,
      trigger: trigger.value,
      entity: entity.value,
      entityCount: entityCount.value,
    },
    t('help.rating.sendSuccess'),
  );

  trackEvent(AnalyticsEvents.rating.starSubmitted, buildRatingEventPayload({ rating: selectedStar.value }));
  trackSubmitRating({ rating: selectedStar.value, step: 'star' });

  starRatingSubmitted.value = true;
};

const trackGoogleReviewOpened = (): void => {
  trackEvent(AnalyticsEvents.rating.googleReviewOpened, buildRatingEventPayload({ rating: selectedStar.value }));
};

const trackFeedbackOpened = (): void => {
  trackEvent(AnalyticsEvents.rating.feedbackOpened, buildRatingEventPayload({ rating: selectedStar.value }));
};

const showFrontPage = computed((): boolean => {
  return !starRatingSubmitted.value;
});

const showRatingSubmitted = computed((): boolean => {
  return starRatingSubmitted.value && !showFinalState.value;
});

const showFinalState = computed((): boolean => {
  return starRatingSubmitted.value && feedbackTextSubmitted.value;
});

defineExpose({
  hide,
  show,
});
</script>
<template>
  <NativeDialog
    ref="dialogRef"
    :class="[
      'rounded-dialog shadow-popover overflow-y-auto overflow-x-hidden w-[480px] border-0 p-0 bg-surface-primary',
      'max-md:fixed max-md:inset-0 max-md:m-0 max-md:h-dvh max-md:max-h-none max-md:w-screen max-md:max-w-none max-md:rounded-none max-md:shadow-none',
    ]"
    :close-on-backdrop-click="false"
    :close-on-cancel="false"
    @backdrop-click="!isMobile ? hide() : undefined"
    @cancel="hide()"
  >
    <div
      :class="[
        'flex flex-col relative',
        'p-[80px] max-md:px-lg max-md:py-xl',
        'w-full min-w-0',
        'max-md:min-h-dvh max-md:justify-center',
      ]"
    >
      <div class="absolute top-[24px] right-[24px]">
        <IconButton type="button" size="button" :icon-name="EIcon.X" theme="text" @click="hide" />
      </div>

      <RatingFrontPage
        v-if="showFrontPage"
        ref="ratingFrontPageRef"
        :entity="entity"
        :entity-count="entityCount"
        @submit-star-rating="submitStarRating"
      />

      <RatingSubmitted
        v-if="showRatingSubmitted"
        :selected-star="selectedStar"
        @open-google-rating="trackGoogleReviewOpened"
        @open-direct-feedback="trackFeedbackOpened"
        @submit-text-feedback="submitTextFeedback"
      />

      <RatingFinal v-if="showFinalState" />
    </div>
  </NativeDialog>
</template>
